import {
  UTM_ADGID,
  UTM_CAMPAIGN,
  UTM_CAT,
  UTM_CONTENT,
  UTM_CTA,
  UTM_EXTERNALID,
  UTM_GEO,
  UTM_MEDIUM,
  UTM_NTWK,
  UTM_SOURCE,
  UTM_SUBSITE,
  UTM_TERM,
} from './constants';
import type { ContextualData, JourneyEventContextualData } from './contextualData';
import {
  emptyContextualData,
  emptyJourneyEventContextualData,
  mergeContextualData,
} from './contextualData';
import type {
  DatadogContext,
  DatadogRumClientInstance,
  JourneyClientInstance,
  SentryClientInstance,
} from './declarations/Clients';
import type { EventTrackingAction, EventTrackingCategory } from './declarations/EventTracking';
import type {
  CategoryName,
  EventData,
  EventName,
  JourneyEventWrapper,
} from './declarations/JourneyEvent';
import type {
  DeployEnv,
  Observability,
  TrackingOptions,
  TrackJourneyEventOptions,
} from './declarations/Observability';
import type { UserEvent } from './declarations/UserEvent';
import { emitDevtoolsEvent } from './devtools-events';
import { globalObservability } from './global';
import { trackEvent as analyticsTrackEvent } from './internal/analytics';
import {
  addRUMGlobalContext,
  getContextualDataFromDatadog,
  trackErrorInDatadog,
  trackEventInDatadog,
  trackJourneyEventInDatadog,
  trackPerformanceInDatadog,
  trackUserEventInDatadog,
} from './internal/datadog';
import { getDeployEnv } from './internal/deployEnv';
import { trackJourneyEventInGTM } from './internal/gtmEvents';
import { trackJourneyEventInJourney, trackUserEventInJourney } from './internal/journey';
import {
  trackErrorInSentry,
  trackEventInSentry,
  trackJourneyEventInSentry,
  trackUserEventInSentry,
} from './internal/sentry';

const OD_UTM_COOKIE = 'od_utm';
type CampaignContext = NonNullable<ContextualData['campaignContext']>;

const getCampaignContextFromSearchParams = (
  searchParams: URLSearchParams,
  includeExtendedUtmFields: boolean,
): CampaignContext => {
  return {
    utmMedium: searchParams.get(UTM_MEDIUM) ?? undefined,
    utmSource: searchParams.get(UTM_SOURCE) ?? undefined,
    utmCampaign: searchParams.get(UTM_CAMPAIGN) ?? undefined,
    utmContent: searchParams.get(UTM_CONTENT) ?? undefined,
    utmTerm: searchParams.get(UTM_TERM) ?? undefined,
    ...(includeExtendedUtmFields
      ? {
          utmAdgid: searchParams.get(UTM_ADGID) ?? undefined,
          utmNtwk: searchParams.get(UTM_NTWK) ?? undefined,
          utmSubsite: searchParams.get(UTM_SUBSITE) ?? undefined,
          utmCta: searchParams.get(UTM_CTA) ?? undefined,
          utmExternalid: searchParams.get(UTM_EXTERNALID) ?? undefined,
          utmGeo: searchParams.get(UTM_GEO) ?? undefined,
          utmCat: searchParams.get(UTM_CAT) ?? undefined,
        }
      : {}),
  };
};

export function readUtmCookie(): CampaignContext | undefined {
  if (typeof document === 'undefined') return undefined;
  try {
    const match = document.cookie.split(';').find((c) => c.trim().startsWith(`${OD_UTM_COOKIE}=`));
    if (!match) return undefined;
    const value = match.split('=').slice(1).join('=');
    return JSON.parse(decodeURIComponent(value.trim()));
  } catch {
    return undefined;
  }
}

/** The real thing. It actually calls services like Google Analytics, Segment, Sentry, etc. */
export class RealObservability<AppCategory, AppActions> implements Observability<
  AppCategory,
  AppActions
> {
  sentryClient: SentryClientInstance = {};
  datadogRumClient: DatadogRumClientInstance = {};
  journeyUserClient: JourneyClientInstance = {};
  journeyEventClient: JourneyClientInstance = {};

  constructor(readonly eventTrackingCategory: EventTrackingCategory<AppCategory>) {}

  setSentryClient(sentryClient: SentryClientInstance): void {
    this.sentryClient = sentryClient;
  }

  getSentryClient(): SentryClientInstance {
    return this.sentryClient;
  }

  setJourneyUserClient(journeyClient: JourneyClientInstance): void {
    this.journeyUserClient = journeyClient;
  }

  setJourneyEventClient(journeyClient: JourneyClientInstance): void {
    this.journeyEventClient = journeyClient;
  }

  getJourneyUserClient(): JourneyClientInstance {
    return this.journeyUserClient;
  }

  getJourneyEventClient(): JourneyClientInstance {
    return this.journeyEventClient;
  }

  setDatadogRumClient(datadogRumClient: DatadogRumClientInstance): void {
    this.datadogRumClient = datadogRumClient;
  }

  getDatadogRumClient(): DatadogRumClientInstance {
    return this.datadogRumClient;
  }

  /**
   * Updates contextual data to add campaign context from URL search params.
   * When UTMs are in the URL, uses them directly (the CF Worker sets the od_utm cookie server-side).
   * When UTMs are not in the URL, falls back to reading the od_utm cookie.
   * This ensures SPA navigations retain campaign attribution from the landing page.
   */
  updateJourneyContextualData(
    contextualData: JourneyEventContextualData = emptyJourneyEventContextualData,
  ): JourneyEventContextualData {
    if (typeof window !== 'undefined') {
      const searchParams = new URLSearchParams(window.location.search);
      const utmParameters = [UTM_MEDIUM, UTM_SOURCE, UTM_CAMPAIGN, UTM_CONTENT, UTM_TERM];
      if (utmParameters.some((param) => searchParams.get(param))) {
        const campaignContext = getCampaignContextFromSearchParams(searchParams, true);
        return { ...contextualData, campaignContext };
      }

      if (!contextualData.campaignContext) {
        const cookieCampaign = readUtmCookie();
        if (cookieCampaign) {
          return { ...contextualData, campaignContext: cookieCampaign };
        }
      }
    }

    return contextualData;
  }
  /**
   * Updates contextual data for non-journey events
   * This will be removed when we remove old versions of tracking data
   */
  updateDeprecatedContextualData(
    contextualData: ContextualData = emptyContextualData,
  ): ContextualData {
    if (typeof window !== 'undefined') {
      const searchParams = new URLSearchParams(window.location.search);
      const utmParameters = [UTM_MEDIUM, UTM_SOURCE, UTM_CAMPAIGN, UTM_CONTENT, UTM_TERM];
      if (utmParameters.some((param) => searchParams.get(param))) {
        const campaignContext = getCampaignContextFromSearchParams(searchParams, false);
        return {
          ...contextualData,
          campaignContext: campaignContext,
        };
      }
    }

    return contextualData;
  }

  /**
   * Track events not performed by a user but meaningful internally. For events performed
   * by a user, use `trackUserEvent`.
   *
   * TODO (@core-experience-platform): rename to `trackInternalEvent`
   */
  trackEvent(
    action: EventTrackingAction<AppActions>,
    label?: string,
    contextualData: ContextualData = emptyContextualData,
    options?: TrackingOptions<AppCategory>,
  ): void {
    contextualData = this.updateDeprecatedContextualData(contextualData);
    // Add contextual data from Datadog (e.g. datadog-rum-session-id).
    contextualData = mergeContextualData(contextualData, getContextualDataFromDatadog());

    if (getDeployEnv() !== 'production') {
      const categoryOverride = options?.categoryOverride ? options.categoryOverride : undefined;
      emitDevtoolsEvent({
        type: 'trackEvent',
        action: action,
        label,
        categoryOverride,
        contextualData: contextualData as unknown as Record<string, unknown>,
      });
    }

    analyticsTrackEvent<AppCategory, AppActions>(
      options?.categoryOverride ?? this.eventTrackingCategory,
      action,
      label,
      contextualData.data,
    );
    trackEventInDatadog<AppActions>(action, label, contextualData);
    trackEventInSentry<AppActions>(action, label, contextualData);

    if (options?.addTiming) {
      trackPerformanceInDatadog(`${this.eventTrackingCategory}-${action}-${label}`);
    }
  }

  /**
   * @deprecated Use trackJourneyEvent instead
   * @see https://knowledgebase.apps.opendoor.com/doc/product/customer-demand/product-eng-org/operations-runbooks/how-to-create-journey-2-0-events.md
   */
  trackUserEvent(event: UserEvent, contextualData: ContextualData = emptyContextualData): void {
    contextualData = this.updateDeprecatedContextualData(contextualData);
    trackUserEventInJourney(event, contextualData);
    trackUserEventInDatadog(event, contextualData);
    trackUserEventInSentry(event, contextualData);
  }

  trackJourneyEvent<C extends CategoryName, E extends EventName<C>>(
    categoryName: C,
    eventName: E,
    eventData: EventData<C, E>,
    contextualData: JourneyEventContextualData = emptyJourneyEventContextualData,
    options?: TrackJourneyEventOptions,
  ): void {
    const eventWrapper: Partial<JourneyEventWrapper> = {};
    eventWrapper[categoryName] = {
      [eventName]: eventData,
    };
    contextualData = this.updateJourneyContextualData(contextualData);

    if (getDeployEnv() !== 'production') {
      emitDevtoolsEvent({
        type: 'trackJourneyEvent',
        categoryName: categoryName,
        eventName: eventName,
        eventData: eventData as unknown as Record<string, unknown>,
        contextualData: contextualData as unknown as Record<string, unknown>,
      });
    }

    const clientEventId =
      typeof crypto !== 'undefined' && crypto.randomUUID ? crypto.randomUUID() : undefined;

    trackJourneyEventInJourney(eventWrapper, contextualData, undefined, clientEventId);
    if (clientEventId) {
      trackJourneyEventInGTM(categoryName, eventName, clientEventId, eventData);
    }
    trackJourneyEventInDatadog(eventWrapper, contextualData);
    trackJourneyEventInSentry(eventWrapper, contextualData);

    if (options?.flush) {
      globalObservability.getJourneyEventClient().send?.();
    }
  }

  trackError(
    label: string,
    error?: Error,
    contextualData: ContextualData = emptyContextualData,
    options?: TrackingOptions<AppCategory>,
  ): void {
    contextualData = this.updateDeprecatedContextualData(contextualData);
    // Add contextual data from Datadog (e.g. datadog-rum-session-id).
    contextualData = mergeContextualData(contextualData, getContextualDataFromDatadog());

    if (!options?.skipSentry) {
      const sentryContextualData = trackErrorInSentry(label, error, contextualData);
      // Add contextual data from Sentry (e.g. sentry-event-id).
      contextualData = mergeContextualData(contextualData, sentryContextualData);
    }

    trackErrorInDatadog(label, error, contextualData);
    analyticsTrackEvent(this.eventTrackingCategory, 'error', label, contextualData.data);

    if (options?.addTiming) {
      trackPerformanceInDatadog(`${this.eventTrackingCategory}-error-${label}`);
    }
  }
}

export const testonly = {
  initDebugObservability(opts: DebugObservabilityProps = {}) {
    globalObservability.setCurrentInstance(new DebugObservability({ log: true, ...opts }));
  },
};

const stringifyForLog = (value: unknown): string =>
  JSON.stringify(value, (_key, v: unknown) => (typeof v === 'bigint' ? v.toString() : v));

// Tests and component fixtures do not initialize observability but still need to satisfy component props.
export function useFakeObservability() {
  return {
    trackJourneyEvent: (
      categoryName: string,
      eventName: string,
      eventData: Record<string, unknown>,
    ) => {
      const eventWrapper = {
        [categoryName]: {
          [eventName]: eventData,
        },
      };
      // eslint-disable-next-line no-console
      console.info(`trackClientJourneyEvent(${stringifyForLog(eventWrapper)})`);
    },
  };
}

export type DebugObservabilityProps =
  | {
      sentryClient?: SentryClientInstance;
      journeyUserClient?: JourneyClientInstance;
      journeyEventClient?: JourneyClientInstance;
      datadogRumClient?: DatadogRumClientInstance;
      log?: boolean;
      sendJourneyEventsInDev?: boolean;
      env?: DeployEnv;
    }
  | undefined;

/** For debugging only. Logs events and errors to console. */
export class DebugObservability<AppCategory, AppActions> implements Observability<
  AppCategory,
  AppActions
> {
  sentryClient: SentryClientInstance = {};
  journeyUserClient: JourneyClientInstance = {};
  journeyEventClient: JourneyClientInstance = {};
  datadogRumClient: DatadogRumClientInstance = {};
  log: boolean = false;
  sendJourneyEventsInDev: boolean = false;
  env: DeployEnv | undefined = getDeployEnv();

  constructor(
    opts: DebugObservabilityProps = {},
    readonly eventTrackingCategory?: EventTrackingCategory<AppCategory>,
  ) {
    this.sentryClient = opts?.sentryClient || {};
    this.journeyUserClient = opts?.journeyUserClient || {};
    this.journeyEventClient = opts?.journeyEventClient || {};
    this.datadogRumClient = opts?.datadogRumClient || {};
    this.log = opts?.log || false;
    this.sendJourneyEventsInDev = opts?.sendJourneyEventsInDev || false;
    this.env = opts?.env;
  }

  /**
   * Updates contextual data to add campaign context from URL search params.
   * When UTMs are in the URL, uses them directly (the CF Worker sets the od_utm cookie server-side).
   * When UTMs are not in the URL, falls back to reading the od_utm cookie.
   * This ensures SPA navigations retain campaign attribution from the landing page.
   */
  updateJourneyContextualData(
    contextualData: JourneyEventContextualData = emptyJourneyEventContextualData,
  ): JourneyEventContextualData {
    if (typeof window !== 'undefined') {
      const searchParams = new URLSearchParams(window.location.search);
      const utmParameters = [UTM_MEDIUM, UTM_SOURCE, UTM_CAMPAIGN, UTM_CONTENT, UTM_TERM];
      if (utmParameters.some((param) => searchParams.get(param))) {
        const campaignContext = getCampaignContextFromSearchParams(searchParams, true);
        return { ...contextualData, campaignContext };
      }

      if (!contextualData.campaignContext) {
        const cookieCampaign = readUtmCookie();
        if (cookieCampaign) {
          return { ...contextualData, campaignContext: cookieCampaign };
        }
      }
    }

    return contextualData;
  }
  /**
   * Updates contextual data for non-journey events
   * This will be removed when we remove old versions of tracking data
   */
  updateDeprecatedContextualData(contextualData: ContextualData = emptyContextualData) {
    if (typeof window !== 'undefined') {
      const searchParams = new URLSearchParams(window.location.search);
      const utmParameters = [UTM_MEDIUM, UTM_SOURCE, UTM_CAMPAIGN, UTM_CONTENT, UTM_TERM];
      if (utmParameters.some((param) => searchParams.get(param))) {
        const campaignContext = getCampaignContextFromSearchParams(searchParams, false);
        return {
          ...contextualData,
          campaignContext: campaignContext,
        };
      }
    }

    return contextualData;
  }

  trackEvent(
    action: EventTrackingAction<AppActions>,
    label?: string,
    contextualData: ContextualData = emptyContextualData,
    options?: TrackingOptions<AppCategory>,
  ): void {
    contextualData = this.updateDeprecatedContextualData(contextualData);
    const categoryOverride = options?.categoryOverride ? options.categoryOverride : undefined;

    emitDevtoolsEvent({
      type: 'trackEvent',
      action: action,
      label,
      categoryOverride,
      contextualData: contextualData as unknown as Record<string, unknown>,
    });

    if (this.log) {
      const categoryPrefix = categoryOverride ? `${categoryOverride}, ` : '';
      console.info(`trackEvent(${categoryPrefix}${action}, ${label})`, contextualData);
    }
  }

  trackUserEvent(event: UserEvent, contextualData: ContextualData = emptyContextualData): void {
    contextualData = this.updateDeprecatedContextualData(contextualData);
    this.log && console.info(`trackUserEvent(${stringifyForLog(event)})`, contextualData);
  }

  trackJourneyEvent<C extends CategoryName, E extends EventName<C>>(
    categoryName: C,
    eventName: E,
    eventData: EventData<C, E>,
    contextualData: JourneyEventContextualData = emptyJourneyEventContextualData,
    options?: TrackJourneyEventOptions,
  ): void {
    const eventWrapper: Partial<JourneyEventWrapper> = {};
    eventWrapper[categoryName] = {
      [eventName]: eventData,
    };
    contextualData = this.updateJourneyContextualData(contextualData);

    emitDevtoolsEvent({
      type: 'trackJourneyEvent',
      categoryName: categoryName,
      eventName: eventName,
      eventData: eventData as unknown as Record<string, unknown>,
      contextualData: contextualData as unknown as Record<string, unknown>,
    });

    const clientEventId =
      typeof crypto !== 'undefined' && crypto.randomUUID ? crypto.randomUUID() : undefined;

    if (this.sendJourneyEventsInDev && this.env === 'development') {
      trackJourneyEventInJourney(
        eventWrapper,
        contextualData,
        this.sendJourneyEventsInDev,
        clientEventId,
      );
      if (clientEventId) {
        trackJourneyEventInGTM(categoryName, eventName, clientEventId, eventData);
      }

      if (options?.flush) {
        globalObservability.getJourneyEventClient().send?.();
      }
    }
    this.log &&
      console.info(`trackJourneyEvent(${stringifyForLog(eventWrapper)})`, {
        clientEventId,
        ...contextualData,
      });
  }

  trackError(
    label: string,
    error?: Error,
    contextualData: ContextualData = emptyContextualData,
  ): void {
    contextualData = this.updateDeprecatedContextualData(contextualData);
    this.log && console.error(`trackError(${label})`, error, contextualData);
  }
  setDatadogRumClient(datadogRumClient: DatadogRumClientInstance): void {
    this.datadogRumClient = datadogRumClient;
  }
  getDatadogRumClient(): DatadogRumClientInstance {
    return this.datadogRumClient;
  }
  setJourneyUserClient(journeyClient: JourneyClientInstance): void {
    this.journeyUserClient = journeyClient;
  }
  setJourneyEventClient(journeyClient: JourneyClientInstance): void {
    this.journeyEventClient = journeyClient;
  }
  getJourneyUserClient(): JourneyClientInstance {
    return this.journeyUserClient;
  }
  getJourneyEventClient(): JourneyClientInstance {
    return this.journeyEventClient;
  }
  setSentClient(sentryClient: SentryClientInstance): void {
    this.sentryClient = sentryClient;
  }
  getSentryClient(): SentryClientInstance {
    return this.sentryClient;
  }
}

/**
 * This will explicitly add contextual data to the global context for Datadog RUM.
 * It is not intended to include everything passed into trackEvent's contextData
 * which is surfaced in custom RUM events and segment. This should be used for
 * things like experimentation treatments, etc.
 */
export const addDatadogRUMGlobalContext = (context: DatadogContext) => {
  addRUMGlobalContext(context);
};
